import localApi from '../../services/localApi'

const NPS_LOGIN_SESSION_KEY = 'forpy:nps:login-session'

export const CURRENT_NPS_VERSION = 'nps-2026-09-v1'

export interface CurrentNpsSurvey {
  version: string
  eligible: boolean
  answered: boolean
  answeredAt: string | null
}

export interface NpsResponseInput {
  score: number
  reportTimeReductionRating: number
  regulatoryConfidenceRating: number
  easeOfUseRating: number
  supportQualityRating: number
  scoreReason?: string
  improvementSuggestion?: string
}

interface NpsResponseData {
  id: string
  version: string
  submittedAt: string
}

interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

function createLoginSessionId (): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function getLoginSessionId (): string {
  try {
    const stored = window.sessionStorage.getItem(NPS_LOGIN_SESSION_KEY)
    if (stored) return stored

    const sessionId = createLoginSessionId()
    window.sessionStorage.setItem(NPS_LOGIN_SESSION_KEY, sessionId)
    return sessionId
  } catch {
    return 'memory-only'
  }
}

function dismissalKey (version: string, userKey: string): string {
  return `forpy:nps:dismissed:${getLoginSessionId()}:${version}:${userKey}`
}

export function startNpsLoginSession (): void {
  try {
    window.sessionStorage.setItem(NPS_LOGIN_SESSION_KEY, createLoginSessionId())
  } catch {
    // The modal still stays closed in memory when session storage is unavailable.
  }
}

export function wasNpsDismissedForSession (version: string, userKey: string): boolean {
  try {
    return window.sessionStorage.getItem(dismissalKey(version, userKey)) === 'true'
  } catch {
    return false
  }
}

export function dismissNpsForSession (version: string, userKey: string): void {
  try {
    window.sessionStorage.setItem(dismissalKey(version, userKey), 'true')
  } catch {
    // In-memory modal state still prevents another prompt until this mount ends.
  }
}

export const npsApi = {
  async getCurrent (): Promise<CurrentNpsSurvey> {
    const response = await localApi.get<ApiResponse<CurrentNpsSurvey>>('/v1/nps/current')
    return response.data.data
  },

  async submit (input: NpsResponseInput): Promise<NpsResponseData> {
    const response = await localApi.post<ApiResponse<NpsResponseData>>('/v1/nps/responses', input)
    return response.data.data
  }
}
